import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  type ChatSessionResponse,
  type ChatSessionUpdate,
  chatGetSessionOptions,
  chatGetSessionQueryKey,
  chatUpdateSessionMutation,
  type ReasoningEffort,
} from "@/api";
import { HISTORY_ATTACHMENTS_PAGE } from "./MessageAttachments";
import { useOnChatSwitch } from "./useChatSwitchKey";

/**
 * The chat session request: each message carries a first page of its attachments plus their count (MHS-646),
 * so a chat with thousands of files loads a bounded response. Use it for every exact cache key of a session.
 */
export const chatSessionRequest = (sessionUuid: string) => ({
  path: { session_uuid: sessionUuid },
  query: { attachmentsLimit: HISTORY_ATTACHMENTS_PAGE },
});

/** An open chat (session + saved messages). Disabled for a new chat. */
export function useChatSession(sessionUuid: string | undefined) {
  return useQuery({
    ...chatGetSessionOptions(chatSessionRequest(sessionUuid ?? "")),
    enabled: Boolean(sessionUuid),
    select: (r) => r.data,
  });
}

/** The cached chat with a PATCH's changes, shown before the server answers. A changed agent or brand drops its name. */
function withChanges(session: ChatSessionResponse, body: ChatSessionUpdate): ChatSessionResponse {
  const next = { ...session };
  if (body.reasoningEffort !== undefined) next.reasoningEffort = body.reasoningEffort;
  if (body.agentUuid !== undefined) Object.assign(next, { agentUuid: body.agentUuid, agentName: null });
  if (body.modelId) next.modelId = body.modelId;
  if (body.brandUuid !== undefined) Object.assign(next, { brandUuid: body.brandUuid, brandName: null });
  return next;
}

/**
 * Saves changes to an open chat optimistically: they show at once, go back if refused (`onRefused` says why), and the
 * chat is read again. One chat's saves go out one after another, so a later one never lands before an earlier one.
 */
export function useSaveChat(sessionUuid: string | undefined, onRefused?: (error: unknown) => void) {
  const queryClient = useQueryClient();
  const key = chatGetSessionQueryKey(chatSessionRequest(sessionUuid ?? ""));
  return useMutation({
    ...chatUpdateSessionMutation(),
    scope: { id: `save-chat-${sessionUuid ?? ""}` },
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{ data: ChatSessionResponse }>(key);
      if (previous) queryClient.setQueryData(key, { ...previous, data: withChanges(previous.data, vars.body) });
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      onRefused?.(error);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/**
 * The chat's thinking level. A new chat holds it locally until the first
 * send; an open chat saves each change to the session (optimistically), and
 * its stored value is never cleared just because models are still loading. The level picked for a new chat stays
 * with it: another chat (`chatKey`, `useChatSwitchKey`) starts on the default again (MHS-1039).
 */
export function useChatEffort(
  sessionUuid: string | undefined,
  session: ChatSessionResponse | undefined,
  chatKey: number,
) {
  const [draftEffort, setDraftEffort] = useState<ReasoningEffort | null>(null);
  useOnChatSwitch(chatKey, () => setDraftEffort(null));
  const save = useSaveChat(sessionUuid);

  if (!sessionUuid) return { effort: draftEffort, setEffort: setDraftEffort };
  return {
    effort: (session?.reasoningEffort ?? null) as ReasoningEffort | null,
    setEffort: (effort: ReasoningEffort | null) =>
      save.mutate({ path: { session_uuid: sessionUuid }, body: { reasoningEffort: effort } }),
  };
}
